import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ProvisioningJobCard } from '../../components/provisioning/ProvisioningJobCard';
import { ProvisioningDlqPanel } from '../../components/provisioning/ProvisioningDlqPanel';
import { Select } from '../../components/clients/form-primitives';
import { PageHelp } from '../../components/common/PageHelp';
import { cn } from '@control-center/ui';
import { useAuth } from '../../hooks/use-auth';
import { useProvisioningJobs } from '../../hooks/use-provisioning';

type StatusFilter = 'pending' | 'in_progress' | 'provisioned';
type Tab = 'queue' | 'failed';

export function ProvisioningQueuePage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // DLQ: admin e financeiro podem reprocessar/resolver/ignorar (controller permite ambos).
  const canManageDlq = user?.role === 'admin' || user?.role === 'financeiro';

  // Story 12.9 AC7 — alerta provisioning_failed linka pra cá com ?tab=failed.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(searchParams.get('tab') === 'failed' ? 'failed' : 'queue');
  const [status, setStatus] = useState<StatusFilter>('pending');
  const query = useProvisioningJobs(status);

  const groups = query.data?.groups ?? [];

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">
              Fila de provisionamento
            </h1>
            <p className="mt-1 text-sm text-muted">
              Após `payment.confirmed`, jobs são criados por entitlement. Marque como provisionado
              quando criar a conta no SaaS. A aba <strong>Falhas</strong> mostra os jobs que
              esgotaram as tentativas automáticas.
            </p>
          </div>
          <PageHelp id="provisioning" />
        </div>
        {!isAdmin && !canManageDlq && (
          <span
            className="rounded-[6px] border border-line bg-panel-2 px-3 py-1 text-[12px] text-muted"
            title="Apenas admin/financeiro podem agir"
          >
            Somente leitura
          </span>
        )}
      </div>

      {/* Abas: Fila (pendentes/provisionados) vs Falhas (DLQ) */}
      <div className="flex items-center gap-1 border-b border-line">
        {(
          [
            { id: 'queue', label: 'Fila' },
            { id: 'failed', label: 'Falhas' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'relative px-3 py-2 text-[13px] font-medium transition-colors',
              tab === t.id
                ? 'text-ink after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-ink'
                : 'text-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'failed' ? (
        <ProvisioningDlqPanel canManage={canManageDlq} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
              <option value="pending">Pendentes</option>
              <option value="in_progress">Em progresso</option>
              <option value="provisioned">Provisionados</option>
            </Select>
            <div className="md:col-span-3 flex items-center justify-end gap-3 text-[12px] text-muted">
              {query.isFetching && <span>Atualizando…</span>}
              {query.data && (
                <span>
                  {query.data.total} job{query.data.total === 1 ? '' : 's'} · {groups.length}{' '}
                  cliente
                  {groups.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>

          {query.isLoading && (
            <div className="rounded-[12px] border border-line bg-panel py-12 text-center text-sm text-muted">
              Carregando jobs…
            </div>
          )}

          {query.isError && (
            <div className="rounded-[12px] border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
              Erro ao carregar jobs. Tente recarregar a página.
            </div>
          )}

          {query.data && groups.length === 0 && (
            <div className="rounded-[12px] border border-good/40 bg-good/10 py-12 text-center text-sm text-ink">
              ✅ Nada para provisionar com este filtro.
            </div>
          )}

          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <section
                key={group.clientId}
                className="rounded-[12px] border border-line bg-panel p-5"
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <Link
                      to={`/identities/${group.clientId}`}
                      className="text-[14px] font-semibold text-ink underline-offset-2 hover:underline"
                    >
                      {group.clientName}
                    </Link>
                    {group.clientEmail && (
                      <div className="text-[11px] text-muted-2">{group.clientEmail}</div>
                    )}
                  </div>
                  <span className="rounded-[6px] border border-line bg-panel-2 px-2 py-[1px] text-[11px] text-muted">
                    {group.jobs.length} job{group.jobs.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {group.jobs.map((job) => (
                    <ProvisioningJobCard key={job.id} job={job} canMarkProvisioned={isAdmin} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
